import { Checkbox, MenuItem, TextField, Typography } from '@mui/material';
import { PdpmEstimateInput, PdpmInputOption } from 'src/services/documentService/pdpmEstimate';
import { bimsOptions, displayValue } from './pdpm-estimator-utils';

/**
 * BRK-1969: the editor for one drawer row. A thin switch on the contract's
 * `valueType` — every row is a controlled input whose displayed value is the
 * pending override if there is one, otherwise the value the backend computed.
 *
 * An unrecognized `valueType` renders as read-only text rather than guessing at
 * a widget, so a kind added by a newer backend degrades instead of breaking.
 *
 * The row (not the editor) renders the visible label, so controls here carry an
 * `aria-label` instead of a MUI floating label. That keeps every control named
 * for screen readers and tests, while avoiding both a duplicated label and MUI's
 * floating label overlapping the value — an outlined Select with `displayEmpty`
 * does not shrink its label on its own.
 */

export const NOT_DOCUMENTED_PLACEHOLDER = 'Not documented — enter to include';

/** Checkbox rows serialize either as 0/1 or as true/false; mirror what we were sent. */
const isChecked = (value: string | null | undefined) => value === '1' || value === 'true';

const checkboxValue = (input: PdpmEstimateInput, checked: boolean): string => {
  const usesBooleanWords = input.value === 'true' || input.value === 'false';
  if (usesBooleanWords) {
    return checked ? 'true' : 'false';
  }
  return checked ? '1' : '0';
};

const optionsFor = (input: PdpmEstimateInput): PdpmInputOption[] => {
  if (input.valueType === 'BIMS') {
    // BIMS has a fixed 00-15 + 99 domain; the backend need not enumerate it
    return input.allowedValues?.length ? input.allowedValues : bimsOptions();
  }
  return input.allowedValues || [];
};

export type PdpmInputEditorProps = {
  input: PdpmEstimateInput;
  /** the pending override for this row, if any */
  overrideValue: string | undefined;
  onOverrideChange: (itemId: string, value: string) => void;
  /** primary dx stays read-only in the MVP; free-text ICD what-ifs need more UX */
  readOnly?: boolean;
  disabled?: boolean;
};

export default function PdpmInputEditor({
  input,
  overrideValue,
  onOverrideChange,
  readOnly,
  disabled,
}: PdpmInputEditorProps) {
  // the override wins while it is pending; once the server echoes it back the
  // two agree, so there is no flicker when the estimate refreshes
  const value = overrideValue ?? input.value ?? '';

  // no control to put a value in, so show the value itself (em-dashed when
  // nothing is documented — see displayValue)
  const readOnlyText = (
    <Typography
      variant="body2"
      color={input.source === 'MISSING' ? 'text.secondary' : 'text.primary'}
    >
      {displayValue(input)}
    </Typography>
  );

  if (readOnly) {
    return readOnlyText;
  }

  switch (input.valueType) {
    case 'CHECKBOX':
    case 'FLAG':
      return (
        <Checkbox
          size="small"
          sx={{ ml: -1 }}
          checked={isChecked(value)}
          disabled={disabled}
          inputProps={{ 'aria-label': input.label }}
          onChange={(event) =>
            onOverrideChange(input.itemId, checkboxValue(input, event.target.checked))
          }
        />
      );

    case 'GG_CODE':
    case 'BIMS':
    case 'ENUM': {
      const options = optionsFor(input);
      // A Select with no domain is not editable — it would render its value as
      // out-of-range and offer only the empty option, so the only "edit" available
      // would be erasing the value. Show the value instead. This is the normal
      // shape for a derived row like CLINICAL_CATEGORY, which the backend computes
      // and sends without allowedValues.
      if (options.length === 0) {
        return readOnlyText;
      }
      return (
        <TextField
          select
          // `outlined` explicitly: the app theme defaults TextField to `filled`,
          // whose layout reserves top padding for a floating label. These rows
          // carry their label above the control, so a filled input would leave a
          // permanently empty gap where the label would have been.
          variant="outlined"
          size="small"
          fullWidth
          disabled={disabled}
          value={value}
          onChange={(event) => onOverrideChange(input.itemId, event.target.value)}
          SelectProps={{
            displayEmpty: true,
            inputProps: { 'aria-label': input.label },
          }}
        >
          {/* a Select never shows TextField's `placeholder`, so the "nothing
              documented" prompt has to be the empty option — which `displayEmpty`
              renders in the closed control too */}
          <MenuItem value="">
            <em>{NOT_DOCUMENTED_PLACEHOLDER}</em>
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
            >
              {option.description || option.value}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    default:
      // unrecognized valueType: show what the backend computed, invent no editor
      return readOnlyText;
  }
}
